import { useEffect } from "preact/hooks";
import { Game } from "./components/Game";
import { HowToPlay } from "./components/HowToPlay";
import { PuzzleStatus } from "./components/PuzzleStatus";
import { ShareGrid } from "./components/ShareGrid";
import { ThemeToggle } from "./components/ThemeToggle";
import { TopBar } from "./components/TopBar";
import { Keyboard } from "./components/Keyboard";
import { MastheadParticles } from "./effects/MastheadParticles";
import { fireCelebration, loadError } from "./game/state";

export function App() {
  // Layout-priority guard: when the playfield can't show every guess
  // row, the share grid yields first. We mark .app with the
  // app--middle-clipped class while .app__middle is overflowing
  // internally; the CSS rule in styles/components/game.css then pins
  // .sharegrid__rows at its 3-row floor (with internal scroll) so
  // every freed pixel falls to the board. When the board does fit, we
  // drop the marker and the share grid is allowed to grow back toward
  // its 5-row ceiling.
  //
  // The "force-remove, measure, conditionally re-add" pattern gives a
  // stable fixpoint: in viewports where 5-row would clip the board but
  // 3-row wouldn't, the marker stays on (re-applied each cycle); in
  // viewports where even 5-row fits, the marker stays off; in
  // viewports where even 3-row can't make the board fit, the marker
  // stays on. ResizeObserver on .app__middle catches viewport changes
  // and ShareGrid-driven bottom-region resizes alike.
  useEffect(() => {
    const app = document.querySelector<HTMLElement>(".app");
    const middle = document.querySelector<HTMLElement>(".app__middle");
    if (app == null || middle == null) return;
    const evaluate = () => {
      app.classList.remove("app--middle-clipped");
      // Read scrollHeight to force a synchronous layout in the
      // un-clipped state before we ask about overflow.
      if (middle.scrollHeight > middle.clientHeight + 1) {
        app.classList.add("app--middle-clipped");
      }
    };
    evaluate();
    const ro = new ResizeObserver(evaluate);
    ro.observe(middle);
    return () => ro.disconnect();
  }, []);

  // Click-to-fire-celebration is a pre-deploy testing affordance only —
  // production renders the masthead as plain art so a stray tap during
  // play can't trigger the win sequence.
  const mastheadStage = import.meta.env.DEV ? (
    <button
      type="button"
      class="app__masthead-stage"
      onClick={() => fireCelebration()}
      aria-label="Play celebration animation"
      tabIndex={-1}
    >
      <img
        class="app__masthead"
        src="/masthead.jpg"
        width="1600"
        height="640"
        alt="PalinDragon"
      />
      <MastheadParticles />
    </button>
  ) : (
    <div class="app__masthead-stage app__masthead-stage--static">
      <img
        class="app__masthead"
        src="/masthead.jpg"
        width="1600"
        height="640"
        alt="PalinDragon"
      />
      <MastheadParticles />
    </div>
  );
  // When the loader has failed (no puzzle today, API down, missing-text
  // wire mismatch), the play surface collapses to just the "Sorry! No
  // puzzle today." placeholder — the toolbar above and the footer
  // below stay, but the reveal-buttons / hint / status TopBar, the
  // keyboard, and the share grid all hide. Game elements have nothing
  // to drive without a puzzle, so showing them invites confusion.
  const failed = loadError.value != null;
  return (
    <div class="app">
      <div class="app__top">
        <header class="app__header">
          <div class="app__toolbar">
            <HowToPlay />
            <PuzzleStatus />
            <ThemeToggle />
          </div>
          {mastheadStage}
          <h1 class="app__title sr-only">PalinDragon</h1>
        </header>
        {!failed && <TopBar />}
      </div>
      <main class="app__middle">
        <Game />
      </main>
      {!failed && (
        <div class="app__bottom">
          <Keyboard />
          <ShareGrid />
        </div>
      )}
      <footer class="app__footer">
        PalinDragon™ — The daily palindrome word game by{" "}
        <a
          href="https://lockedlab.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          Locked Lab
        </a>
        {" • "}
        <a href="mailto:palindragon@lockedlab.com?subject=PalinDragon%20Feedback">
          Send us feedback!
        </a>
      </footer>
    </div>
  );
}
